
import React from "react";
import { useAuth } from "./context/AuthContext";
import { UserPreferencesModal } from "./components/UserPreferencesModal";
import { 
  canSeeModule, 
  canViewWorklog, 
  canViewAuditLog, 
  canUseDebugView,
  DEBUG_VIEW_DEPT_CODES,
  getDeptCodeByEmail
} from "./config/roles";
import { DEPT_CODE_TO_NAME, getDeptNameByCode } from "./constants/departments";

export type NavKey =
  | "dashboard"
  | "tasksAndLogs"
  | "contractReviews"
  | "legalMatters"
  | "ipMatters"
  | "brandAssets"
  | "lawyerCoop"
  | "sealApplication"
  | "templates"
  | "governanceCoreDocs"
  | "audit"
  | "calendar"
  | "govCorrespondence"
  | "notificationHistory"
  | "storageDebug"
  | "contracts"
  | "complianceControlCenter" // NEW KEY
  | "legacyContracts";

const ALL_ITEMS: Array<{ key: NavKey; label: string; moduleKey?: any }> = [
  { key: "dashboard", label: "法務總攬儀錶板", moduleKey: "OTHERS" },
  { key: "tasksAndLogs", label: "待辦事項與法務工作日誌", moduleKey: "TASKS_AND_LOG" },
  { key: "contractReviews", label: "合約審查、管理及制式範本系統", moduleKey: "CONTRACTS" },
  { key: "sealApplication", label: "用印申請與紀錄", moduleKey: "SIGN_SYSTEM" },
  { key: "legalMatters", label: "案件及爭議管理", moduleKey: "CASES" },
  { key: "ipMatters", label: "智財申請與維權", moduleKey: "IP" },
  { key: "brandAssets", label: "品牌資產管理", moduleKey: "BRAND_ASSETS" }, 
  { key: "lawyerCoop", label: "顧問律師管理", moduleKey: "LAWYER" },
  { key: "govCorrespondence", label: "公部門來文及函覆", moduleKey: "GOV_CORRESPONDENCE" },
  { key: "complianceControlCenter", label: "法遵情資與專案管控中心", moduleKey: "REG_IMPACT_TRACKING" }, // NEW LABEL
  { key: "governanceCoreDocs", label: "公司治理核心文件庫", moduleKey: "GOV_FILES" },
  { key: "legacyContracts", label: "舊合約登錄系統", moduleKey: "LEGACY_ENTRY" },
  { key: "calendar", label: "行事曆", moduleKey: "OTHERS" },
  { key: "audit", label: "稽核、簽核及操作紀錄", moduleKey: "AUDIT" }, 
  { key: "storageDebug", label: "系統檢測 (Debug)", moduleKey: "OTHERS" },
];

const DEPT_MINIMAL_VISIBLE_KEYS = new Set([
  "governanceCoreDocs",
  "sealApplication",
  "contractReviews",
  "complianceControlCenter" // Updated
]);

// Special labels for specific accounts (Local part of email)
const SUPER_USER_LABELS: Record<string, string> = {
  kc: "法務部：Kevin",
  ad: "管理員：Admin",
  lisa: "主管：Lisa",
  irene: "主管：Irene",
  ken: "主管：Ken",
  king: "主管：King",
};

type Props = {
  active: NavKey;
  onSelect: (k: NavKey) => void;
  role: string;
  username: string;
  onLogout: () => void;
  onOpenSettings: () => void;
  onOpenShortcuts: () => void;
};

export function Sidebar({ active, onSelect, onLogout }: Props) {
  const { user, debugViewDepartmentId, setDebugViewDepartmentId } = useAuth();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [showPrefs, setShowPrefs] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const userRole = user?.role || 'staff';
  const email = user?.email || "";

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayedItems = ALL_ITEMS.filter(item => {
    if (item.key === 'legacyContracts') {
        const isLegacyRole = userRole === 'legacyDataEntry';
        const isTargetEmail = ["kc@lg.app", "ad@lg.app"].includes(email.toLowerCase());
        return isLegacyRole || isTargetEmail;
    }

    if (userRole === 'legacyDataEntry') {
        return false;
    }

    if (userRole === 'dept-minimal') {
      return DEPT_MINIMAL_VISIBLE_KEYS.has(item.key);
    }

    if (item.key === 'tasksAndLogs') {
       return canViewWorklog(email);
    }
    if (item.key === 'audit') {
       return canViewAuditLog(email);
    }

    if (item.key === 'storageDebug' && userRole !== 'superAdmin' && userRole !== 'legal') return false;
    
    return canSeeModule(userRole, item.moduleKey);
  });

  const currentUserEmail = email.toLowerCase();
  const localPart = currentUserEmail.split("@")[0];
  let identityLabel = "";
  
  if (user) {
      // 1. Check Special Accounts first
      if (SUPER_USER_LABELS[localPart]) {
          identityLabel = SUPER_USER_LABELS[localPart];
      } else {
          // 2. Fallback to Department Logic
          const deptCode = getDeptCodeByEmail(currentUserEmail);
          const deptName = getDeptNameByCode(deptCode);
          
          if (deptName === "OTHER" || deptCode === "OTHER") {
               identityLabel = "一般使用者";
          } else {
               identityLabel = `${deptName}`;
          }
      }
  } else {
      identityLabel = "訪客";
  }

  const canDebug = canUseDebugView(email);

  return (
    <aside className="sidebar" style={{ display: "flex", flexDirection: "column" }}>
      <div className="brand">
        <b>LegalOps</b>
        <span className="badge" style={{ fontSize: 10 }}>v5.0.0</span>
      </div>

      {canDebug && (
        <div style={{ marginBottom: 12, padding: 8, background: 'rgba(59, 130, 246, 0.1)', borderRadius: 8, border: '1px dashed #3b82f6' }}>
           <div style={{fontSize:11, color:'#60a5fa', marginBottom:4}}>👁️ 部門視角切換 (Debug)</div>
           <select 
             className="input" 
             style={{fontSize:12, padding:6, height: 'auto', background: '#111'}}
             value={debugViewDepartmentId || ""}
             onChange={(e) => setDebugViewDepartmentId(e.target.value || null)}
           >
             <option value="">(預設) 實際帳號視角</option>
             {DEBUG_VIEW_DEPT_CODES.map(code => (
               <option key={code} value={code}>{code} - {DEPT_CODE_TO_NAME[code] || code}</option>
             ))}
           </select>
        </div>
      )}

      <div className="nav" style={{ flex: 1, overflowY: "auto" }}>
        {displayedItems.map((it) => (
          <button
            key={it.key}
            className={"navbtn" + (active === it.key ? " active" : "")}
            onClick={() => onSelect(it.key)}
          >
            {it.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 14, position: "relative" }} ref={menuRef}>
        <hr />
        <button 
          className="navbtn" 
          style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span style={{ fontSize: 13 }}>👤 {identityLabel}</span>
          <span style={{ fontSize: 10 }}>{menuOpen ? "▼" : "▲"}</span>
        </button>

        {menuOpen && (
          <div style={{
            position: "absolute",
            bottom: "100%", left: 0, right: 0,
            background: "#1e1e20",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: 4,
            marginBottom: 8,
            boxShadow: "var(--shadow)",
            zIndex: 50,
            display: "flex", flexDirection: "column", gap: 2
          }}>
            <MenuItem onClick={() => { setShowPrefs(true); setMenuOpen(false); }}>⚙️ 個人偏好設定</MenuItem>
            <div style={{ borderTop: "1px solid #333", margin: "4px 0" }}></div>
            <MenuItem onClick={onLogout} style={{ color: "var(--danger)" }}>🚪 登出</MenuItem>
          </div>
        )}
      </div>

      {showPrefs && <UserPreferencesModal onClose={() => setShowPrefs(false)} />}
    </aside>
  );
}

function MenuItem({ children, onClick, disabled, style }: any) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        background: "transparent", border: "none", color: disabled ? "#555" : "var(--text)",
        textAlign: "left", padding: "8px 10px", borderRadius: 4, cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 13, ...style
      }}
      onMouseEnter={(e) => !disabled && (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
      onMouseLeave={(e) => !disabled && (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </button>
  );
}
